import {
  AVG_CHAR_WIDTH_FACTOR,
  LINE_HEIGHT_FACTOR,
  RICH_LINE_HEIGHT_FACTOR,
} from './pdfLayoutConstants';

/**
 * Approximates jsPDF `splitTextToSize` line wrapping. The microservice measures real
 * glyph widths; we use an average advance per character which is accurate enough for a
 * page-count estimate (the goal is a ±10% band, not pixel-perfect layout).
 */
export function estimateWrappedLineCount(
  text: string,
  fontSize: number,
  maxWidth: number
): number {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return 0;
  }

  const charWidth = fontSize * AVG_CHAR_WIDTH_FACTOR;
  const charsPerLine = Math.max(1, Math.floor(maxWidth / charWidth));

  // Wrap word-by-word: a word that does not fit starts a new line, mirroring how
  // splitTextToSize keeps words intact (long words still overflow onto extra lines).
  const words = trimmed.split(/\s+/);
  let lines = 1;
  let lineLength = 0;

  for (const word of words) {
    const wordLength = word.length;
    const additional = lineLength === 0 ? wordLength : wordLength + 1;

    if (lineLength + additional <= charsPerLine) {
      lineLength += additional;
      continue;
    }

    if (wordLength > charsPerLine) {
      // Word longer than a line: it consumes ceil(word / line) lines on its own.
      lines += Math.ceil(wordLength / charsPerLine);
      lineLength = 0;
      continue;
    }

    lines += 1;
    lineLength = wordLength;
  }

  return lines;
}

/** Height of plain text (`Text`) given its content and the available width. */
export function estimatePlainTextHeight(
  text: string,
  fontSize: number,
  maxWidth: number
): number {
  const lines = estimateWrappedLineCount(text, fontSize, maxWidth);
  if (lines === 0) {
    return 0;
  }
  return lines * fontSize * LINE_HEIGHT_FACTOR;
}

/**
 * Height of an approximate single line of text at the given font size — used for labels
 * and known one-line values (dashes, dates, short enums).
 */
export function singleLineHeight(fontSize: number): number {
  return fontSize * LINE_HEIGHT_FACTOR;
}

/** Height of rich text (`RichText`) wrapped to `maxWidth`. */
export function estimateRichTextHeight(
  charCount: number,
  fontSize: number,
  maxWidth: number
): number {
  if (charCount <= 0) {
    return 0;
  }
  const charWidth = fontSize * AVG_CHAR_WIDTH_FACTOR;
  const charsPerLine = Math.max(1, Math.floor(maxWidth / charWidth));
  const lines = Math.max(1, Math.ceil(charCount / charsPerLine));
  return lines * fontSize * RICH_LINE_HEIGHT_FACTOR;
}
